import clsx from 'clsx'
import {cloneDeep, compact, get, isEmpty, isEqual, map, truncate, uniqBy, uniqueId} from 'lodash'
import moment from 'moment'
import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {useIntl} from 'react-intl'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {
  CUSTOM_COLUMN_NAME,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  TABLE_KEY_COLUMN,
  ROUTES,
  WEIGHT_UNIT,
} from '../../../../_gori/constants'
import {
  KTSVG,
  convertUserTimeZone,
  getShipmentFees,
  setLoadingActionTable,
  useDisabled,
  formatOmitLongText,
  toAbsoluteUrl,
} from '../../../../_gori/helpers'
import {blockPopUp} from '../../../../_gori/helpers/BrowserHelpers'
import {FilterOrderSkeleton, TableSkeleton} from '../../../../_gori/helpers/components'
import {useMeter} from '../../../../_gori/helpers/components/MeterProvider'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import {TooltipModal} from '../../../../_gori/helpers/components/TooltipModal'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {
  Button,
  CarrierBadge,
  ConfirmActionSwal,
  DropdownButton,
  Table,
  TableBody,
  TableTabs,
  TableWrapper,
  ValidationErrorModal,
  RequiredVerifyEmail,
} from '../../../../_gori/partials/widgets'
import {ParcelBadge} from '../../../../_gori/partials/widgets/mixed/ParcelBadge'
import {showArrCustomColumns} from '../../../../_gori/utils/common'
import {useAuth} from '../../auth'
import {
  FromToEditModal,
  NoticePrintPackingSlips,
  OrderChildModal,
  OrderFilter,
  PackagesTableModal,
  RatesModal,
  TrackingDetailModal,
  PackageInfoModal,
  TrackingNumbersDetail,
} from '../../orders'
import SettingsService from '../../settings/core/_requests'
import {ShipmentRefund} from '../../shipments/core/_const'
import ShipmentService from '../../shipments/core/_requests'
import {
  HIDDEN_COLUMNS_BY_CANCELLED,
  HIDDEN_COLUMNS_BY_DEFAULT,
  HIDDEN_COLUMNS_BY_ON_HOLD,
  HIDDEN_COLUMNS_BY_OPEN_AND_AWAITING_PAYMENT,
  HIDDEN_COLUMNS_BY_SHIPPED,
  ORDER_COLUMN_KEYS,
  OrderStatus,
  OrderType,
  SYNC_STATUS,
} from '../core/_const'
import OrderHelper from '../core/_helpers'
import OrderService from '../core/_requests'
import {NoticeModals} from '../../dashboard'
import A1_CONSTANTS from '../../a1/core/_const'
import {COMMON_OUTER_HEIGHT} from '../../../../_gori/constants'
import {
  convertOrderCopy,
  getParcelCount,
  getTotalWeight,
  getWeightUnit,
} from '../../../../_gori/utils'
import {OrderTrackingNumberCell} from './order-child/OrderTrackingNumberCell'
import {GetStarted} from './GetStarted'
import {EmptyTable} from '../../common/components/EmptyTable'
import {useOrderRowActions} from '../hooks/useOrderRowActions'
import {OrderRowActionModals} from './OrderRowActionModals'

export const AddressView = ({
  data,
  prefix = null,
  classNames = 'text-gray-800',
  locationClassNames = 'text-gray-500',
  wrapperClassName = '',
}: {
  data: any
  prefix?: string | null
  classNames?: string | null
  locationClassNames?: string | null
  wrapperClassName?: string
}) => {
  const company = get(data, prefix ? `${prefix}_company` : 'company')
  const firstName = get(data, prefix ? `${prefix}_first_name` : 'first_name', '')
  const lastName = get(data, prefix ? `${prefix}_last_name` : 'last_name', '')
  const fullName = compact([firstName, lastName]).join(' ')
  const city = get(data, prefix ? `${prefix}_city` : 'city', '')
  const state = get(data, prefix ? `${prefix}_state` : 'state', '')
  const location = compact([city, state]).join(', ')

  return (
    <span className={wrapperClassName}>
      <p className={clsx('m-0', classNames)}>{isEmpty(company) ? fullName : company}</p>
      <p className={clsx('m-0', locationClassNames)}>{location}</p>
    </span>
  )
}

export const TooltipAddress = ({
  row,
  dataLength,
  handleEdit,
}: {
  row: any
  dataLength: number
  handleEdit: () => void
}) => {
  const intl = useIntl()
  const isLastRow = row.index === dataLength - 1

  return (
    <TooltipModal
      className={clsx('d-flex flex-column', {
        'tooltip-modal--top': isLastRow,
        'tooltip-modal--bottom': !isLastRow,
      })}
      classNameTooltip={clsx('text-dark text-hover-primary', {
        'tooltip-modal__text--top': isLastRow,
        'tooltip-modal__text--bottom': !isLastRow,
      })}
      children={<AddressView data={row.original} prefix='to' />}
      textTooltip={intl.formatMessage({id: 'EDIT_ADDRESS'})}
      handleEvent={handleEdit}
    />
  )
}

export const TooltipBatchName = ({
  row,
  dataLength,
  batchName,
  className,
  to,
}: {
  row: any
  dataLength: number
  batchName: string
  className: string
  to: string
}) => {
  const navigate = useNavigate()
  const isLastRow = row.index === dataLength - 1
  const children = (
    <>
      <div
        onClick={() => navigate(to)}
        className={`text-gray-800 mb-1 pe-3 ps-3 text-hover-primary cursor-pointer ${className}`}
      >
        {truncate(batchName, {
          length: 17,
          omission: '...',
        })}
      </div>
    </>
  )
  return (
    <TooltipModal
      className={clsx('d-flex flex-column', {
        'tooltip-modal--top': isLastRow,
        'tooltip-modal--bottom': !isLastRow,
      })}
      classNameTooltip={clsx('text-dark text-hover-primary text-max-content', {
        'tooltip-modal__text--top': isLastRow,
        'tooltip-modal__text--bottom': !isLastRow,
      })}
      children={children}
      textTooltip={batchName}
      handleEvent={() => {
        navigate(to)
      }}
    />
  )
}

const OrderPage: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {routes} = UseRoutesPermission()
  const {setPageDisabled} = useDisabled()
  const {loadingSwitch, currentUser} = useAuth()
  const {statesGlobal, syncState, setSyncState} = useStatesGlobal()
  const {setMeterChange} = useMeter()
  const [searchParams, setSearchParams] = useSearchParams()
  const orderHelper = OrderHelper()
  const carriers = statesGlobal.carriers
  const columnsShow =
    statesGlobal?.manageColumns?.[
      CUSTOM_COLUMN_NAME?.ORDER_PAGE?.[searchParams.get('status')?.toUpperCase() || 'ALL']
    ]
  const {newCancelToken, isCancel} = useCancelToken()
  const [loadingFirst, setLoadingFirst] = useState<Boolean>(true)
  const [selectedListId, setSelectedListId] = useState<any>([])
  const [selectedMaster, setSelectedMaster] = useState<boolean>(false)
  const [integrationStores, setIntegrationStores] = useState<any>([])
  const [tableData, setTableData] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const [statistics, setStatistics] = useState([])
  const [validationErrors, setValidationErrors] = useState<any>()
  const [settings, setSettings] = useState<any>([])

  const [showModal, setShowModal] = useState<{
    confirmActionSwal: 'RESTORE' | 'REMOVE_HOLD' | undefined
    fromToEdit: boolean
    packageTable: boolean
    rateLabel: boolean
    createLabel: boolean
    outdated: boolean
    noticePrintPackingSlip: boolean
    trackingDetail: boolean
    packageInfoTable: boolean
    trackingNumbersDetail: boolean
    requiredVerifyEmail: boolean
  }>({
    confirmActionSwal: undefined,
    fromToEdit: false,
    packageTable: false,
    rateLabel: false,
    createLabel: false,
    outdated: false,
    noticePrintPackingSlip: false,
    trackingDetail: false,
    packageInfoTable: false,
    trackingNumbersDetail: false,
    requiredVerifyEmail: false,
  })

  const hasSelectedA1 = useMemo(() => {
    return selectedListId.some((item) => !!item.original?.a1_batch_id)
  }, [selectedListId])

  const [dataModal, setDataModal] = useState<any>({
    confirmActionSwal: null,
    fromToEdit: {},
    packageTable: {},
    rateLabel: {},
    createLabel: {},
    editOrder: {},
    noticePrintPackingSlip: {},
    trackingDetail: {},
    packageInfoTable: {},
    trackingNumbersDetail: [],
  })

  const initFilterValue = useMemo(
    () => ({
      start: moment().subtract(29, 'days').startOf('day').format(),
      end: moment().endOf('day').format(),
    }),
    []
  )

  const setTable = useCallback(async () => {
    setPageDisabled(true)
    let package_type: any = []
    let package_id: any = []
    const _packageInfo = searchParams.get('package_type') || null
    if (_packageInfo) {
      _packageInfo.split(',').forEach((packageItem) => {
        const convertPackage = Number.parseInt(packageItem)
        if (isEqual(convertPackage, NaN)) {
          package_type.push(packageItem)
        } else {
          package_id.push(packageItem)
        }
      })
    }

    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
        search_text: searchParams.get('search_text') || null,
        sort_by: searchParams.get('sort_by') || null,
        sort_direction: searchParams.get('sort_direction') || null,
        status: searchParams.get('status') || null,
        batch_id: searchParams.get('batch_id') || null,
        store_type: searchParams.get('store_type') || null,
        store_id: searchParams.get('store_id') || null,
        // BEGIN: Order filters
        date_from: searchParams.get('date_from') || initFilterValue.start,
        date_to: searchParams.get('date_to') || initFilterValue.end,
        batch: searchParams.get('batch') || null,
        a1_batch: searchParams.get('a1_batch') || null,
        ship_by_date_from: searchParams.get('ship_by_date_from') || null,
        ship_by_date_to: searchParams.get('ship_by_date_to') || null,
        customer: searchParams.get('customer') || null,
        order_ref: searchParams.get('order_ref') || null,
        item_sku: searchParams.get('item_sku') || null,
        item_name: searchParams.get('item_name') || null,
        item_quantity: searchParams.get('item_quantity') || null,
        service: searchParams.get('service') || null,
        preset_id: searchParams.get('preset_id') || null,
        assigned_id: searchParams.get('assigned_id') || null,
        package_type: package_type?.join(',') || null,
        package_id: package_id?.join(',') || null,
        tag_id: searchParams.get('tag_id') || null,
        region: searchParams.get('region') || null,
        order_note: searchParams.get('order_note') || null,
        carriers: searchParams.get('carrier') || null,
        // END: Order filters
      },
      cancelToken: newCancelToken(),
    }

    await OrderService.search(config)
      .then((response: any) => {
        const newOrder = Array.from(response.orders, (order: any) => {
          return {
            ...order,
            canCheck: true,
          }
        })

        setTableData(newOrder)
        setPagination(response.pagination)
        setStatistics(response.statistics)
      })
      .catch((error) => {
        if (isCancel(error)) return
      })
      .finally(() => {
        setPageDisabled(false)
        setLoadingFirst(false)
      })
  }, [initFilterValue, searchParams, setPageDisabled, isCancel, newCancelToken])

  const rowActionOptions = useMemo(
    () => ({
      onAfterCancel: () => setTable(),
      onAfterAssign: () => setTable(),
      onAfterRefund: () => setTable(),
      onAfterHold: () => setTable(),
    }),
    [setTable]
  )

  const {
    getActionMenuItems,
    dataModal: rowActionDataModal,
    modalHandlers,
  } = useOrderRowActions(rowActionOptions)

  const getIntegrationStores = useCallback(async () => {
    setPageDisabled(true)

    try {
      const response = await SettingsService.connections({cancelToken: newCancelToken()})
      if (response.connections?.shopify?.length > 0 && !showModal.outdated) {
        response.connections.shopify.forEach((store) => {
          if (store.outdated) {
            setShowModal((prev) => ({...prev, outdated: store.outdated}))
          }
        })
      }
      setIntegrationStores(response.connections)
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setPageDisabled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken, setPageDisabled])

  const getSettings = useCallback(async () => {
    const settings = await SettingsService.settings({cancelToken: newCancelToken()})
    setSettings(settings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return

    setTable()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, setTable])

  useEffect(() => {
    if (loadingSwitch) return

    getIntegrationStores()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, getIntegrationStores])

  useEffect(() => {
    if (loadingSwitch) return

    getSettings()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, getSettings])

  useEffect(() => {
    if (
      (syncState.type === SYNC_STATUS.SUCCEEDED || syncState.type === SYNC_STATUS.CANCELLED) &&
      syncState.shouldReload
    ) {
      setSyncState((prev) => ({...prev, shouldReload: false, retryDone: false}))
      setTable()
    }
  }, [syncState.type, syncState.shouldReload, setTable, setSyncState])

  useEffect(() => {
    if (!searchParams.get('status')) {
      searchParams.set('status', 'open')
      setSearchParams(searchParams, {replace: true})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRemoveHoldOrder = useCallback(
    async (orderId: any) => {
      setTableData(setLoadingActionTable(tableData, [orderId], true))

      let payload = {
        order_ids: [orderId],
      }

      try {
        const res = await OrderService.removeHold(payload, {cancelToken: newCancelToken()})
        if (res) {
          toast.success(intl.formatMessage({id: 'REMOVED_HOLD_ORDER_SUCCESSFULLY'}))
          setTable()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setTableData(setLoadingActionTable(tableData, [orderId], false))
      }
    },
    [intl, isCancel, newCancelToken, setTable, tableData]
  )

  const handleRestoreOrder = useCallback(
    async (orderId: any) => {
      setTableData(setLoadingActionTable(tableData, [orderId], true))
      let payload = {
        order_ids: [orderId],
      }

      try {
        const res = await OrderService.restore(payload, {cancelToken: newCancelToken()})
        if (res) {
          toast.success(intl.formatMessage({id: 'RESTORED_ORDER_SUCCESSFULLY'}))
          setTable()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setTableData(setLoadingActionTable(tableData, [orderId], false))
      }
    },
    [intl, isCancel, newCancelToken, setTable, tableData]
  )

  const handleViewOrder = useCallback(
    (orderId: any) => {
      const currentUrl = `${window.location.pathname}${window.location.search}`
      navigate(`${ROUTES.ORDERS.BASE}/${orderId}`, {
        state: {fromUrl: currentUrl},
      })
    },
    [navigate]
  )

  const handleEditModalRate = useCallback((data) => {
    const _valueOrder = Object.entries(cloneDeep(data)).reduce(
      (initValue, [key, value]: [any, any]) => {
        switch (true) {
          case key.includes('international_option'):
            initValue.international_option = value
            break
          case key.includes('insurance_option'):
            initValue.insurance_option = value
            break
          case key.includes('signature_confirmation'):
            initValue.signature_confirmation = value
            break
          case key.includes('from_'):
            initValue.from[key] = value
            break
          case key.includes('to_'):
            initValue.to[key] = value
            break
          case key === 'parcels':
            initValue.parcels = value
            break
          case key === 'service':
            initValue.service = `${data.carrier}_${data.service}`
            break
          case key === 'id':
            initValue.id = value
            break
          case key === 'preset_id':
            initValue.preset_id = data.preset_id
            break
          case key === 'extra_service':
            initValue.extra_service = data?.extra_service?.code
            break
          default:
            break
        }

        return initValue
      },
      {
        international_option: {},
        insurance_option: {},
        signature_confirmation: null,
        from: {},
        to: {},
        parcels: {},
        service: '',
        id: '',
        preset_id: null,
        extra_service: null,
      }
    )
    setDataModal((prev) => ({...prev, rateLabel: _valueOrder}))
    setShowModal((prev) => ({...prev, rateLabel: true}))
  }, [])

  const handlePrintLabel = useCallback(
    async (dataOrder: any) => {
      const shipment = dataOrder?.latest_shipment
      const isRefunded =
        shipment?.refund?.status === ShipmentRefund.SUBMITTED ||
        shipment?.refund?.status === ShipmentRefund.REFUNDED
      if (isRefunded) {
        toast.warning(
          intl.formatMessage({
            id: 'PRINT_LABEL_REFUNDED',
          })
        )
        return
      }
      setTableData(setLoadingActionTable(tableData, [dataOrder.id], true))
      const config = {cancelToken: newCancelToken()}
      const data = {
        order_ids: [dataOrder.id],
      }
      await OrderService.print(data, config)
        .then((response) => {
          if (blockPopUp(response.label_url)) {
            toast.warning(
              intl.formatMessage({
                id: 'POP_UP_IS_BLOCKED',
              })
            )
          }
        })
        .catch((error: any) => {
          if (isCancel(error)) return
          setValidationErrors(error?.response)
        })
      setTableData(setLoadingActionTable(tableData, [dataOrder.id], false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, intl, tableData]
  )

  const handlePrintPackingSlip = useCallback(
    async (dataOrder: any) => {
      if (!settings?.packing_slip) return
      const {
        packing_slip: {disable},
      } = settings
      if (disable) {
        const configNoticePackingSlip = {
          title: 'PRINT_PACKING_SLIP',
          content: 'DISABLE_PACKING_SLIPS_MESSAGE',
        }
        setDataModal((prev) => ({...prev, noticePrintPackingSlip: configNoticePackingSlip}))
        setShowModal((prev) => ({...prev, noticePrintPackingSlip: true}))
        return
      }

      setTableData(setLoadingActionTable(tableData, [dataOrder.id], true))
      try {
        let payload = {
          order_id: dataOrder.id,
        }
        const res = await ShipmentService.storePackingSlip(payload, {
          cancelToken: newCancelToken(),
        })
        if (res) {
          if (blockPopUp(res.packing_slip_url)) {
            toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
          }
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setTableData(setLoadingActionTable(tableData, [dataOrder.id], false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, intl, tableData, settings]
  )

  const handleCopy = useCallback(
    async (id) => {
      if (!id) return
      setTableData(setLoadingActionTable(tableData, [id], true))
      try {
        const {order} = await OrderService.get(id, {cancelToken: newCancelToken()})
        if (order) {
          navigate(ROUTES.ORDERS.CREATE, {
            state: {dataCopy: convertOrderCopy(order)},
          })
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setTableData(setLoadingActionTable(tableData, [id], false))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, navigate, newCancelToken, tableData]
  )

  const handleGetRate = useCallback(
    async (data) => {
      try {
        setTableData(setLoadingActionTable(tableData, [data.id], true, 'isLoadingRate'))
        const res = await OrderService.estimateRate(
          {
            order_id: data?.id,
          },
          {cancelToken: newCancelToken()}
        )
        if (res) {
          setTable()
        }
      } catch (error: any) {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      } finally {
        setTableData(setLoadingActionTable(tableData, [data.id], false, 'isLoadingRate'))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, orderHelper, setTable, tableData]
  )

  const actionHandlers = useMemo(
    () => ({
      onPrintLabel: (orderData: any) => handlePrintLabel(orderData),
      onPrintPackingSlip: (orderData: any) => handlePrintPackingSlip(orderData),
      onCopy: (orderData: any) => handleCopy(orderData?.id || orderData),
    }),
    [handlePrintLabel, handlePrintPackingSlip, handleCopy]
  )

  const columns = useMemo(() => {
    const _columns = [
      {
        id: TABLE_KEY_COLUMN.CAN_CHECK,
        Header: intl.formatMessage({id: 'ID'}),
        accessor: 'canCheck',
      },
      {
        id: ORDER_COLUMN_KEYS.CREATED_AT,
        Header: intl.formatMessage({id: 'ORDER_DATE'}),
        accessor: 'created_at',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.created_at,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.CODE,
        Header: 'BAE ID',
        accessor: 'code',
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return (
            <div
              className={clsx(
                'text-gray-800 mb-1 d-inline',
                routes.ORDERS_UPDATE.hasPermission || routes.ORDERS_VIEW.hasPermission
                  ? 'text-hover-primary cursor-pointer'
                  : 'cursor-no-drop'
              )}
              onClick={() =>
                routes.ORDERS_UPDATE.hasPermission || routes.ORDERS_VIEW.hasPermission
                  ? handleViewOrder(row.original.id)
                  : false
              }
            >
              {row.original.code}
            </div>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.SHIPPED_DATE,
        Header: intl.formatMessage({id: 'SHIPPED_DATE'}),
        accessor: 'shipped_date',
        headerClassName: 'min-w-125px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return (
            !isEmpty(row.original.shipped_date) &&
            convertUserTimeZone(
              row.original.shipped_date,
              currentUser,
              process.env.REACT_APP_DATE_FORMAT
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.HOLD_UNTIL_DATE,
        Header: intl.formatMessage({id: 'HOLD_BY_DATE'}),
        accessor: 'hold_until_date',
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return (
            !isEmpty(row.original?.hold_until_date) &&
            convertUserTimeZone(
              row.original?.hold_until_date,
              currentUser,
              process.env.REACT_APP_DATE_FORMAT
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.CANCELLED_AT,
        Header: intl.formatMessage({id: 'CANCELLED_DATE'}),
        accessor: 'cancelled_at',
        headerClassName: 'min-w-150px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return (
            !isEmpty(row.original?.cancelled_at) &&
            convertUserTimeZone(
              row.original?.cancelled_at,
              currentUser,
              process.env.REACT_APP_DATE_FORMAT
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.SHIP_BY_DATE,
        Header: intl.formatMessage({id: 'SHIP_BY'}),
        accessor: 'ship_by_date',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return (
            !isEmpty(row.original?.ship_by_date) &&
            convertUserTimeZone(
              row.original?.ship_by_date,
              currentUser,
              process.env.REACT_APP_DATE_FORMAT
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.A1_BATCH,
        Header: 'A1',
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-center',
        Cell: (data) => {
          const row = data.row
          const batchName = row.original?.a1_batch?.name
          const to = `${ROUTES.A1.BASE}/${row.original.a1_batch_id}?status=${A1_CONSTANTS.A1_BATCH_STATUS.OPEN}`
          return (
            !isEmpty(row.original.a1_batch) && (
              <>
                <div className='mb-3' onClick={() => navigate(to)}>
                  <KTSVG path='media/gori/a1/logo_order.svg' className='text-primary svg-icon-1' />
                </div>
                <TooltipBatchName
                  row={row}
                  dataLength={data.rows.length}
                  batchName={batchName}
                  className='bg-light-info'
                  to={to}
                />
              </>
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.BATCH,
        Header: intl.formatMessage({id: 'BATCH'}),
        accessor: 'batch',
        headerClassName: 'min-w-150px w-250px',
        Cell: (data) => {
          const row = data.row
          const batchName = row.original.batches[0]?.name
          const to = `${ROUTES.BATCHES.BASE}/${row.original.batches[0]?.id}${
            searchParams.get('status') ? `?status=${searchParams.get('status')}` : ''
          }`
          return (
            !isEmpty(row.original.batches) && (
              <TooltipBatchName
                row={row}
                dataLength={data.rows.length}
                batchName={batchName}
                className='badge-light'
                to={to}
              />
            )
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.CUSTOMER,
        Header: intl.formatMessage({id: 'CUSTOMER'}),
        accessor: 'customer',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap mw-150px',
        Cell: (data) => {
          const row = data.row
          const isTooltipVisible =
            row.original.status === OrderStatus.OPEN && routes.ORDERS_UPDATE.hasPermission

          const handleEdit = () => {
            setDataModal((prev: any) => ({...prev, fromToEdit: row.original}))
            setShowModal((prev: any) => ({...prev, fromToEdit: true}))
          }
          return isTooltipVisible ? (
            <TooltipAddress row={row} dataLength={data.rows.length} handleEdit={handleEdit} />
          ) : (
            <>
              {row.original?.latest_shipment ? (
                <AddressView data={row.original.latest_shipment} prefix='to' />
              ) : (
                <AddressView data={row.original} prefix='to' />
              )}
            </>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.STATUS,
        Header: intl.formatMessage({id: 'STATUS'}),
        accessor: 'status',
        headerClassName: 'min-w-175px text-center',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => {
          return (
            <span
              className={`order-status-badge-${row.original?.status?.replaceAll(
                '_',
                '-'
              )} px-4 py-2 rounded-pill`}
            >
              {intl.formatMessage({id: row.original.status.toUpperCase()})}
            </span>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.PACKAGE_INFO,
        Header: intl.formatMessage({id: 'PACKAGE_INFO'}),
        headerClassName: 'min-w-100px text-center',
        cellClassName: 'text-center',
        Cell: (data) => {
          const row = data.row
          const originalParcels = row.original?.parcels || []
          const shipmentParcels = row.original?.latest_shipment?.parcels || []

          const countOriginalParcels = getParcelCount(originalParcels)
          const totalOriginalWeight = getTotalWeight(originalParcels)
          const originalWeightUnit = getWeightUnit(originalParcels)

          const countShipmentParcels = getParcelCount(shipmentParcels)
          const totalShipmentWeight = getTotalWeight(shipmentParcels)
          const shipmentWeightUnit = getWeightUnit(shipmentParcels)

          const isLastRow = data.rows.length - 1 === row.index
          const canEdit =
            row.original.status === OrderStatus.OPEN && routes.ORDERS_UPDATE.hasPermission

          const renderBadge = (count, total, unit, parcels) => (
            <div className='badge badge-light'>
              <span
                className='cursor-pointer mx-autofw-bold fs-8 bagde badge-light py-1 px-3 rounded-1 text-lowercase lh-sm'
                onClick={() => {
                  setDataModal((prev) => ({...prev, packageInfoTable: parcels}))
                  setShowModal((prev) => ({...prev, packageInfoTable: true}))
                }}
              >
                {count} {intl.formatMessage({id: 'PACKAGES'})} <br />
                <span>{total + ' ' + unit + ' ' + intl.formatMessage({id: 'TOTAL'})}</span>
              </span>
            </div>
          )

          const renderParcelBadge = (parcel) => (
            <ParcelBadge key={uniqueId('parcel')} values={parcel} />
          )

          const renderAddButton = () => (
            <div className='flex-shrink-0'>
              <Button
                className='btn border-active btn-white btn-sm text-gray-700'
                event={() => {
                  setDataModal((prev) => ({...prev, packageTable: row.original}))
                  setShowModal((prev) => ({...prev, packageTable: true}))
                }}
              >
                <i className='fas fa-plus' />
                {intl.formatMessage({id: 'ADD_PACKAGE_INFO'})}
              </Button>
            </div>
          )

          // Editable Tooltip Mode
          if (canEdit) {
            return (
              <TooltipModal
                className={clsx({
                  'tooltip-modal--top': isLastRow,
                  'tooltip-modal--bottom': !isLastRow,
                })}
                classNameTooltip={clsx('text-dark text-hover-primary w-120px', {
                  'tooltip-modal__text--top': isLastRow,
                  'tooltip-modal__text--bottom': !isLastRow,
                })}
                children={
                  <>
                    {countOriginalParcels > 1 &&
                      renderBadge(
                        countOriginalParcels,
                        totalOriginalWeight,
                        originalWeightUnit,
                        originalParcels
                      )}
                    {countOriginalParcels === 1 && renderParcelBadge(originalParcels[0])}
                    {countOriginalParcels === 0 && renderAddButton()}
                  </>
                }
                textTooltip={
                  intl.formatMessage({id: 'VIEW'}) + '\n' + intl.formatMessage({id: 'EDIT'})
                }
                handleEvent={() => {
                  setDataModal((prev) => ({...prev, packageTable: row.original}))
                  setShowModal((prev) => ({...prev, packageTable: true}))
                }}
              />
            )
          }

          // Read-only Mode
          if (shipmentParcels.length > 0) {
            return countShipmentParcels > 1
              ? renderBadge(
                  countShipmentParcels,
                  totalShipmentWeight,
                  shipmentWeightUnit,
                  shipmentParcels
                )
              : renderParcelBadge(shipmentParcels[0])
          }

          if (originalParcels.length > 0) {
            return countOriginalParcels > 1
              ? renderBadge(
                  countOriginalParcels,
                  totalOriginalWeight,
                  originalWeightUnit,
                  originalParcels
                )
              : renderParcelBadge(originalParcels[0])
          }

          return canEdit ? renderAddButton() : null
        },
      },
      {
        id: ORDER_COLUMN_KEYS.TOTAL_WEIGHT,
        Header: intl.formatMessage({id: 'WEIGHT'}),
        headerClassName: 'min-w-100px text-center',
        accessor: 'total_weight',
        cellClassName: 'text-center',
        Cell: (data) => {
          const row = data.row
          const totalLatestShipmentWeight =
            row.original?.latest_shipment?.parcels?.reduce(
              (total, parcel) => total + (parcel.weight * parcel.package_count || 0),
              0
            ) || 0
          const latestShipmentWeightUnit =
            row.original?.latest_shipment?.parcels[0].weight_unit ?? WEIGHT_UNIT.OZ

          const totalOriginalWeight =
            Math.round(
              (row.original?.parcels?.reduce(
                (total, parcel) => total + (parcel.weight * parcel.package_count || 0),
                0
              ) || 0) * 100
            ) / 100
          const originalWeightUnit =
            row.original?.parcels?.length > 0
              ? row.original.parcels[0].weight_unit ?? WEIGHT_UNIT.OZ
              : WEIGHT_UNIT.OZ

          return (
            <>
              {row.original?.latest_shipment ? (
                <>
                  <span>{totalLatestShipmentWeight + ' ' + latestShipmentWeightUnit}</span>
                  <br />
                </>
              ) : row.original.parcels.length > 0 ? (
                <>
                  <span>{totalOriginalWeight + ' ' + originalWeightUnit}</span>
                  <br />
                </>
              ) : (
                ''
              )}
            </>
          )
        },
      },
      // Hidden per UX/Ui Designer -- Uncomment if we want to enable
      // {
      //   id: 'carrier',
      //   Header: intl.formatMessage({id: 'CARRIER'}),
      //   headerClassName: 'min-w-80px text-center',
      //   cellClassName: 'text-center',
      //   Cell: ({row}: {row: any}) => {
      //     const carrier = row.original?.latest_shipment?.carrier || row.original?.carrier
      //     return carrier ? (
      //       <span className='badge badge-light-primary'>{carrier.toUpperCase()}</span>
      //     ) : null
      //   },
      // },
      {
        id: ORDER_COLUMN_KEYS.RATES,
        Header: intl.formatMessage({id: 'RATES'}),
        headerClassName: 'min-w-50px text-center',
        cellClassName: 'text-center',
        Cell: (data) => {
          const row = data.row
          let sum = 0
          let service = carriers?.[row.original.carrier + '_' + row.original.service] ?? []
          if (row.original?.latest_shipment) {
            sum = getShipmentFees(row.original?.latest_shipment?.fees?.items)
            service =
              carriers?.[
                row.original?.latest_shipment?.carrier +
                  '_' +
                  row.original?.latest_shipment?.service
              ] ?? []
          } else if (row.original?.estimated_rate) {
            sum = row.original?.estimated_rate
          }
          const isCheapest = row.original?.is_cheapest_rate ?? false

          return row.original.status === OrderStatus.OPEN && routes.ORDERS_UPDATE.hasPermission ? (
            row.original.carrier ? (
              <TooltipModal
                className={clsx({
                  'tooltip-modal--top': data.rows.length - 1 === row.index,
                  'tooltip-modal--bottom': data.rows.length - 1 !== row.index,
                })}
                classNameTooltip={clsx('text-dark text-hover-primary w-150px', {
                  'tooltip-modal__text--top': data.rows.length - 1 === row.index,
                  'tooltip-modal__text--bottom': data.rows.length - 1 !== row.index,
                })}
                children={
                  <>
                    <CarrierBadge
                      handleGetRate={() => handleGetRate(row.original)}
                      loadingRate={row.original.isLoadingRate}
                      service={service}
                      isCheapest={isCheapest}
                      {...((row.original?.latest_shipment || row.original?.estimated_rate) && {
                        cost: sum,
                      })}
                    />
                  </>
                }
                textTooltip={intl.formatMessage({id: 'EDIT_RATE'})}
                handleEvent={() => {
                  handleEditModalRate(row.original)
                }}
              />
            ) : (
              <div className='flex-shrink-0'>
                <Button
                  className='btn border-active btn-white btn-sm text-dark text-hover-primary'
                  event={() => {
                    handleEditModalRate(row.original)
                  }}
                >
                  <i className='fas fa-sync' />
                  {intl.formatMessage({id: 'GET_RATES'})}
                </Button>
              </div>
            )
          ) : (
            <>
              {row.original.carrier && (
                <CarrierBadge
                  service={service}
                  isCheapest={isCheapest}
                  {...((row.original?.latest_shipment || row.original?.estimated_rate) && {
                    cost: sum,
                  })}
                />
              )}
            </>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.OUT_FOR_DELIVERY,
        Header: intl.formatMessage({id: 'OUT_FOR_DELIVERY'}),
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-center',
        Cell: (data) => {
          const row = data.row
          const ShipmentTrackingStatus = row.original?.latest_shipment?.status_label ?? null
          const ShipmentTracking = row.original?.latest_shipment?.shipment_tracking ?? null
          return (
            <div>
              {ShipmentTrackingStatus ? (
                <div className='justify-content-center'>
                  <span className='fw-bolder'>{ShipmentTrackingStatus}</span>
                  <br />
                  {ShipmentTracking && (
                    <span
                      className='cursor-pointer text-decoration-underline text-muted mt-4'
                      onClick={() => {
                        setShowModal((prev) => ({...prev, trackingDetail: true}))
                        setDataModal((prev) => ({...prev, trackingDetail: ShipmentTracking}))
                      }}
                    >
                      {intl.formatMessage({id: 'SEE_DETAIL'})}
                      {'>'}
                    </span>
                  )}
                </div>
              ) : (
                ''
              )}
            </div>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.TRACKING_NUMBER,
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-100px text-center',
        cellClassName: 'text-center',
        Cell: (data) => {
          if (isEmpty(carriers)) return null
          const row = data.row
          let service = carriers?.[row.original.carrier + '_' + row.original.service] ?? []
          const isMultiParcel = row.original?.latest_shipment?.tracking_numbers
            ? row.original?.latest_shipment?.tracking_numbers.length > 1
            : false

          const trackingNumbers = row.original?.latest_shipment
            ? row.original?.latest_shipment?.tracking_numbers
            : null
          return (
            <OrderTrackingNumberCell
              onClickTrackingNumbersDetail={() => {
                setShowModal((prev) => ({...prev, trackingNumbersDetail: true}))
                setDataModal((prev) => ({
                  ...prev,
                  trackingNumbersDetail: row,
                }))
              }}
              service={service}
              trackingNumbers={trackingNumbers}
              isMultiParcel={isMultiParcel}
            />
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.REFERENCER_1,
        Header: intl.formatMessage({id: 'ORDER_REF'}),
        accessor: 'reference1',
        headerClassName: 'text-wrap min-w-125px',
        Cell: ({row}: {row: any}) => {
          return <>{row.original.reference1}</>
        },
      },
      {
        id: ORDER_COLUMN_KEYS.ITEM_NAME,
        Header: intl.formatMessage({id: 'ITEM_NAME'}),
        accessor: 'item_name',
        headerClassName: 'text-wrap min-w-125px',
        Cell: ({row}: {row: any}) => {
          const arr = map(row.original.order_items, 'title')
          return (
            <div>
              {arr.map((item, index) => (
                <div key={index}>{formatOmitLongText(item)}</div>
              ))}
            </div>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.ITEM_SKU,
        Header: intl.formatMessage({id: 'ITEM_SKU'}),
        accessor: 'item_sku',
        headerClassName: 'text-wrap min-w-125px',
        Cell: ({row}: {row: any}) => {
          const arr = map(uniqBy(row.original.order_items, 'sku'), 'sku')

          return (
            <div>
              {arr.map((item, index) => (
                <div key={index}>{formatOmitLongText(item)}</div>
              ))}
            </div>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.ITEM_QUANTITY,
        Header: intl.formatMessage({id: 'ITEM_QUANTITY'}),
        accessor: (row) => {
          const orderItems = row.order_items || []
          return orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
        },
        headerClassName: 'text-wrap text-center min-w-125px',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => {
          const orderItems = row.original.order_items || []
          const totalQuantity = orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
          const finalQuantity = totalQuantity > 0 ? totalQuantity : ''
          return <div>{finalQuantity}</div>
        },
      },
      {
        id: ORDER_COLUMN_KEYS.ASSIGNED_NAME,
        Header: intl.formatMessage({id: 'ASSIGNED'}),
        accessor: 'assigned_name',
        headerClassName: 'min-w-150px text-center',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => {
          if (!isEmpty(row.original.assigned_name)) {
            return (
              <span className={`  assigned-badge- px-4 py-2 rounded-pill`}>
                {row.original.assigned_name}
              </span>
            )
          } else {
            return <span className={`assigned-badge px-4 py-2 rounded-pill`}></span>
          }
        },
      },
      {
        id: ORDER_COLUMN_KEYS.TAG,
        Header: intl.formatMessage({id: 'TAG'}),
        headerClassName: 'min-w-150px',
        Cell: (data) => {
          const row = data.row
          return (
            <div className='d-flex'>
              {row.original.tags.map((tag, idx) => (
                <TooltipModal
                  className={clsx('me-2', {
                    'tooltip-modal--top': data.rows.length - 1 === row.index,
                    'tooltip-modal--bottom': data.rows.length - 1 !== row.index,
                  })}
                  classNameTooltip={clsx('text-dark mt-2', {
                    'tooltip-modal__text--top': data.rows.length - 1 === row.index,
                    'tooltip-modal__text--bottom': data.rows.length - 1 !== row.index,
                  })}
                  children={
                    <KTSVG
                      key={idx}
                      path='/media/gori/orders/tag.svg'
                      className='svg-icon-2'
                      style={{color: tag.color}}
                    />
                  }
                  textTooltip={tag.name}
                  handleEvent={() => {}}
                />
              ))}
            </div>
          )
        },
      },
      {
        id: ORDER_COLUMN_KEYS.STORE_NAME,
        Header: intl.formatMessage({id: 'STORE'}),
        accessor: 'store_name',
        headerClassName: 'text-wrap min-w-125px',
        Cell: ({row}: {row: any}) => {
          return !isEmpty(row.original?.store?.store_name)
            ? row.original?.store?.store_name
            : intl.formatMessage({id: 'SHIPBAE'})
        },
      },
      {
        id: ORDER_COLUMN_KEYS.NOTE,
        Header: intl.formatMessage({id: 'ORDER_NOTE'}),
        accessor: 'note',
        headerClassName: 'text-wrap min-w-125px',
        Cell: ({row}: {row: any}) => {
          return !isEmpty(row.original?.note) ? row.original?.note : ''
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        headerClassName: 'min-w-150px fixed-action',
        cellClassName: 'fixed-action',
        Cell: ({row}: {row: any}) => {
          return (
            <div>
              {
                <>
                  <div
                    className={clsx({
                      'cursor-no-drop': row.original.isLoading,
                    })}
                    id={'group' + row.index}
                  >
                    <Button
                      className={clsx('btn btn-advanced-primary btn-sm fs-6 rounded-2 w-150px', {
                        'pe-none': row.original.isLoading || !isEmpty(row.original.a1_batch),
                      })}
                      disabled={row.original.isLoading || !isEmpty(row.original.a1_batch)}
                      event={() => {
                        if (
                          row.original.status === OrderStatus.OPEN &&
                          routes.SHIPMENTS_CREATE.hasPermission
                        ) {
                          if (!currentUser?.email_verified) {
                            setShowModal((prev) => ({...prev, requiredVerifyEmail: true}))
                          } else {
                            setDataModal((prev) => ({...prev, createLabel: row.original.id}))
                            setShowModal((prev) => ({...prev, createLabel: true}))
                          }
                        } else if (
                          row.original.status === OrderStatus.CANCELLED &&
                          routes.ORDERS_CANCEL.hasPermission
                        ) {
                          setDataModal((prev) => ({...prev, confirmActionSwal: row.original.id}))
                          setShowModal((prev) => ({...prev, confirmActionSwal: 'RESTORE'}))
                        } else if (
                          row.original.status === OrderStatus.ON_HOLD &&
                          routes.ORDERS_HOLD.hasPermission
                        ) {
                          setDataModal((prev) => ({...prev, confirmActionSwal: row.original.id}))
                          setShowModal((prev) => ({...prev, confirmActionSwal: 'REMOVE_HOLD'}))
                        } else {
                          handleViewOrder(row.original.id)
                        }
                      }}
                    >
                      {row.original.status === OrderStatus.OPEN &&
                      routes.SHIPMENTS_CREATE.hasPermission
                        ? intl.formatMessage({id: 'CREATE_LABEL'})
                        : row.original.status === OrderStatus.CANCELLED &&
                          routes.ORDERS_CANCEL.hasPermission
                        ? intl.formatMessage({id: 'RESTORE'})
                        : row.original.status === OrderStatus.ON_HOLD &&
                          routes.ORDERS_HOLD.hasPermission
                        ? intl.formatMessage({id: 'REMOVE_HOLD'})
                        : intl.formatMessage({id: 'VIEW'})}
                    </Button>
                  </div>
                </>
              }
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.SUB_ACTIONS,
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          const isEditable = [OrderStatus.OPEN, OrderStatus.ON_HOLD].includes(row.original.status)

          const listDropDownBtn = [
            {
              label: intl.formatMessage({id: 'VIEW_ORDER'}),
              action: () => {
                navigate(`${ROUTES.ORDERS.BASE}/${row.original.id}`)
              },
              className: 'cursor-pointer',
              hidden: !isEditable || !routes.ORDERS_UPDATE.hasPermission,
            },
            {
              label: intl.formatMessage({id: 'RESHIP'}),
              action: () => {
                setDataModal((prev) => ({...prev, createLabel: row.original.id}))
                setShowModal((prev) => ({...prev, createLabel: true}))
              },
              className: 'cursor-pointer',
              hidden:
                row.original.status !== OrderStatus.SHIPPED ||
                !routes.SHIPMENTS_CREATE.hasPermission,
            },
            ...getActionMenuItems(row.original, {
              handlers: actionHandlers,
              refund: row.original?.refund,
            }).map((item) => ({
              label: item.label,
              action: item.action,
              className: 'cursor-pointer',
              hidden: item.hidden,
            })),
          ]

          return (
            <DropdownButton
              loading={row.original.isLoading}
              list={listDropDownBtn}
              className='d-flex justify-content-center'
            />
          )
        },
      },
    ]
    return _columns.filter((col) => col)
  }, [
    intl,
    searchParams,
    currentUser,
    routes,
    handleViewOrder,
    navigate,
    carriers,
    handleGetRate,
    handleEditModalRate,
    actionHandlers,
    getActionMenuItems,
  ])

  const setIsShowColumns = useCallback(
    (columns) => {
      return cloneDeep(columns).map((item: any) => {
        item.show = true
        switch (searchParams.get('status')) {
          case null: //all
            if (HIDDEN_COLUMNS_BY_DEFAULT.includes(item.id)) {
              item.show = false
            }
            break
          case 'open':
          case 'awaiting_payment':
            if (HIDDEN_COLUMNS_BY_OPEN_AND_AWAITING_PAYMENT.includes(item.id)) {
              item.show = false
            }
            break
          case 'shipped':
            if (HIDDEN_COLUMNS_BY_SHIPPED.includes(item.id)) {
              item.show = false
            }
            break
          case 'on_hold':
            if (HIDDEN_COLUMNS_BY_ON_HOLD.includes(item.id)) {
              item.show = false
            }
            break
          case 'cancelled':
            if (HIDDEN_COLUMNS_BY_CANCELLED.includes(item.id)) {
              item.show = false
            }
            break
        }
        return item
      })
    },
    [searchParams]
  )

  const customColumns = useMemo(() => {
    if (columnsShow) {
      return showArrCustomColumns(columns, columnsShow)
    } else {
      return setIsShowColumns(columns)
    }
  }, [columns, columnsShow, setIsShowColumns])

  const handleModalSuccess = useCallback(
    (key: 'createLabel' | 'fromToEdit' | 'packageTable' | 'rateLabel') => {
      switch (key) {
        case 'createLabel':
          setMeterChange(true)
          toast.success(intl.formatMessage({id: 'CREATE_LABEL_SUCCESSFULLY'}))
          break
        default:
          toast.success(intl.formatMessage({id: 'UPDATED_SUCCESSFULLY'}))
          break
      }
      setShowModal((prev) => ({...prev, [key]: false}))
      setTable()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, setMeterChange, setTable]
  )

  const handleConfirmActionSwal = useCallback(() => {
    switch (showModal.confirmActionSwal) {
      case 'REMOVE_HOLD':
        handleRemoveHoldOrder(dataModal.confirmActionSwal)
        break
      case 'RESTORE':
        handleRestoreOrder(dataModal.confirmActionSwal)
        break
    }
    setDataModal((prev) => ({...prev, confirmActionSwal: null}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal])

  const hasThirdPartyStore = useMemo(() => {
    const storeType = searchParams.get('store_type')
    const storeId = searchParams.get('store_id')
    const isNotShipBae = storeType !== OrderType.SHIPBAE

    return (storeType || storeId) && isNotShipBae
  }, [searchParams])

  const isShowGettingStarted = useMemo(() => {
    return !currentUser?.existed_order
  }, [currentUser?.existed_order])

  const [dataAutoSetting, setDataAutoSetting] = useState<any>({
    status: false,
    value: 0,
  })

  const getSettingInsurance = useCallback(async () => {
    try {
      const response = await SettingsService.getSettingInsurance()
      setDataAutoSetting({
        status: response?.data?.status ?? false,
        value: response?.data?.value ?? 0,
      })
    } catch (error) {
      setDataAutoSetting({
        status: false,
        value: 0,
      })
    }
  }, [])

  useEffect(() => {
    getSettingInsurance()
  }, [getSettingInsurance])

  return (
    <>
      <ConfirmActionSwal
        show={!!showModal.confirmActionSwal}
        title={intl.formatMessage({id: 'RESTORE'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleConfirmActionSwal}
        handleClose={() => setShowModal((prev) => ({...prev, confirmActionSwal: undefined}))}
      />
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      {showModal.outdated && routes.ADMINS.hasPermission && (
        <ConfirmActionSwal
          show={showModal.outdated}
          title={intl.formatMessage({id: 'OUTDATED_SHOPIFY_APP'})}
          message={intl.formatMessage({id: 'OUTDATED_SHOPIFY_APP'})}
          messageCancel={intl.formatMessage({id: 'NO'})}
          handleCallBack={() => navigate(ROUTES.SETTINGS.CONNECTIONS.SHOPIFY)}
          handleClose={() => setShowModal((prev) => ({...prev, outdated: false}))}
        />
      )}

      {showModal.fromToEdit && (
        <FromToEditModal
          show={showModal.fromToEdit}
          data={dataModal.fromToEdit}
          handleClose={() => setShowModal((prev) => ({...prev, fromToEdit: false}))}
          handleToEditSuccess={() => handleModalSuccess('fromToEdit')}
          disabledFrom={!!dataModal.fromToEdit.a1_batch_id}
        />
      )}

      {showModal.packageTable && (
        <PackagesTableModal
          show={showModal.packageTable}
          data={dataModal.packageTable}
          handleClose={() => setShowModal((prev) => ({...prev, packageTable: false}))}
          handlePackagesTableEditSuccess={() => handleModalSuccess('packageTable')}
        />
      )}

      {showModal.packageInfoTable && (
        <PackageInfoModal
          show={showModal.packageInfoTable}
          data={dataModal.packageInfoTable}
          handleClose={() => setShowModal((prev) => ({...prev, packageInfoTable: false}))}
        />
      )}

      {showModal.rateLabel && (
        <RatesModal
          show={showModal.rateLabel}
          data={dataModal.rateLabel}
          handleClose={() => setShowModal((prev) => ({...prev, rateLabel: false}))}
          handleEditRateLabelSuccess={() => handleModalSuccess('rateLabel')}
        />
      )}

      <OrderRowActionModals dataModal={rowActionDataModal} modalHandlers={modalHandlers} />

      {showModal.createLabel && (
        <OrderChildModal
          show={showModal.createLabel}
          handleClose={() => {
            setTable()
            setShowModal((prev) => ({...prev, createLabel: false}))
            setDataModal((prev) => ({...prev, createLabel: null}))
          }}
          handleSuccess={(url) => {
            if (blockPopUp(url)) {
              toast.warning(
                intl.formatMessage({
                  id: 'POP_UP_IS_BLOCKED',
                })
              )
            }
            handleModalSuccess('createLabel')
          }}
          parentId={dataModal.createLabel}
        />
      )}

      {showModal.noticePrintPackingSlip && (
        <NoticePrintPackingSlips
          show={showModal.noticePrintPackingSlip}
          handleClose={() => setShowModal((prev) => ({...prev, noticePrintPackingSlip: false}))}
          configModal={dataModal.noticePrintPackingSlip}
        />
      )}

      {showModal.trackingDetail && (
        <TrackingDetailModal
          show={showModal.trackingDetail}
          handleClose={() => setShowModal((prev) => ({...prev, trackingDetail: false}))}
          data={dataModal.trackingDetail}
        />
      )}
      {showModal.trackingNumbersDetail && (
        <TrackingNumbersDetail
          show={showModal.trackingNumbersDetail}
          data={dataModal.trackingNumbersDetail}
          handleClose={() => setShowModal((prev) => ({...prev, trackingNumbersDetail: false}))}
        />
      )}
      {showModal.requiredVerifyEmail && (
        <RequiredVerifyEmail
          show={showModal.requiredVerifyEmail}
          closeModal={() => {
            setShowModal((prev) => ({...prev, requiredVerifyEmail: false}))
          }}
        />
      )}
      <NoticeModals />
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <TableWrapper>
              <div className='table-content--inset-inner h-100'>
                <FilterOrderSkeleton />
                <TableBody>
                  <TableSkeleton />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <>
            <TableTabs
              dataTabs={statistics}
              keyCheckActive='status'
              classNameTabs='order-tabs'
              classNameChildTabs='col-auto mb-3'
              children={
                <TableWrapper className='rounded-top-left-0-dt'>
                  <div className='table-content--inset-inner h-100'>
                    <OrderFilter
                      selectedList={{
                        selected: selectedListId,
                        selectedMaster: selectedMaster,
                        total: pagination?.total,
                      }}
                      statistics={statistics}
                      reloadTable={() => {
                        setMeterChange(true)
                        setTable()
                      }}
                      dataSettings={settings}
                      dataAutoSetting={dataAutoSetting}
                      integrationStores={integrationStores}
                    />
                    {hasSelectedA1 && (
                      <div className='d-flex align-items-center gap-1 px-3 py-1 bg-light-danger mx-8 mb-6 align-self-start'>
                        <img
                          alt='err'
                          src={toAbsoluteUrl('/media/gori/invalid.svg')}
                          width={16}
                          height={20}
                        />
                        <span className='fs-6 text-danger'>
                          {intl.formatMessage({id: 'MASS_UPDATE_EXCLUDE_A1'})}
                        </span>
                      </div>
                    )}
                    <TableBody height={`calc(100vh - ${COMMON_OUTER_HEIGHT}px)`}>
                      {isEmpty(tableData) && !isShowGettingStarted && (
                        <EmptyTable imgSrc='/media/gori/orders/empty-box.svg' />
                      )}
                      {!(isEmpty(tableData) || isShowGettingStarted) && (
                        <Table
                          columns={customColumns}
                          data={tableData}
                          pagination={pagination}
                          tbodyClass='text-gray-600 fw-bold'
                          usePagination
                          useCheckBox={!isEmpty(tableData)}
                          callBackSetData={(selected) => {
                            setSelectedMaster(selected?.length === pagination?.page_size)
                            setSelectedListId(selected)
                          }}
                          customColumn={{
                            show: true,
                            name: CUSTOM_COLUMN_NAME?.ORDER_PAGE?.[
                              searchParams.get('status')?.toUpperCase() || 'ALL'
                            ],
                          }}
                        />
                      )}
                    </TableBody>
                    {isShowGettingStarted && <GetStarted />}
                  </div>
                </TableWrapper>
              }
              childrenTab={
                // <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
                <div className='d-flex justify-content-between'>
                  <div className='d-flex flex-wrap gap-3'>
                    {routes.BATCHES_VIEW.hasPermission && (
                      <Link to={ROUTES.BATCHES.BASE} className='btn btn-advanced-secondary border'>
                        <KTSVG
                          path='/media/gori/orders/upload_csv.svg'
                          className='me-2 svg-icon-2'
                        />
                        {intl.formatMessage({id: 'UPLOAD_CSV'})}
                      </Link>
                    )}
                    {!hasThirdPartyStore && routes.ORDERS_CREATE.hasPermission && (
                      <Link to={ROUTES.ORDERS.CREATE} className='btn btn-advanced-primary border'>
                        <KTSVG path='/media/gori/orders/create_order.svg' className='me-2' />
                        {intl.formatMessage({id: 'CREATE_ORDER'})}
                      </Link>
                    )}
                  </div>
                </div>
                // </CSSTransition>
              }
            />
          </>
        )}
      </CSSTransition>
    </>
  )
}

export {OrderPage}
